import { useEffect, useMemo, useState } from 'react';
import { Boxes, Plus, Calendar, ChevronLeft, ChevronRight, Search, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { InsumoVista, MovimientoStockVista, HistoricoDiaVista } from '@shared/types';
import { NumberField } from '@renderer/components/ui/NumberField';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourInventario } from '@renderer/tours';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { InsumoCard, calcularSemaforo, type Semaforo } from '@renderer/components/inventario/InsumoCard';
import { ModalCargaInsumo } from '@renderer/components/inventario/ModalCargaInsumo';
import { ModalCorregirExistencia } from '@renderer/components/inventario/ModalCorregirExistencia';
import { AvisoInvendibles } from '@renderer/components/inventario/AvisoInvendibles';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { toLocalYYYYMMDD } from '@renderer/utils/dateLocal';

// Sprint 15 — Rediseño de /inventario a grid de tarjetas.
// Anterior: tabla densa con 5+ columnas y bulk-save al fondo.
// Nuevo:    grid responsive con semáforo prominente y carga 1×1 desde cada
//           card (alineado con el flujo real: la dueña recibe pedidos uno por
//           uno del proveedor y los va cargando conforme llegan).

type FiltroSemaforo = 'todos' | 'agotado' | 'critico' | 'bajo' | 'ok';

export function InventarioDelDiaPage() {
  const navigate = useNavigate();
  const [insumos, setInsumos] = useState<InsumoVista[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<FiltroSemaforo>('todos');
  const [vistaBitacora, setVistaBitacora] = useState<{ insumoId: number; nombre: string; movs: MovimientoStockVista[] } | null>(null);
  const [mermaModal, setMermaModal] = useState<{ insumoId: number; nombre: string; unidad: string } | null>(null);
  const [cargaModal, setCargaModal] = useState<InsumoVista | null>(null);
  /** Sprint 133 — "Me equivoqué al ingresar las cantidades, ¿cómo puedo corregir?" (la dueña). */
  const [corrigiendo, setCorrigiendo] = useState<InsumoVista | null>(null);

  const cargar = async () => {
    const res = await window.api.insumos.listar();
    if (res.ok) setInsumos(res.data);
  };

  useEffect(() => {
    cargar();
  }, []);

  const insumosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return insumos
      .filter((i) => (q ? i.nombre.toLowerCase().includes(q) : true))
      .filter((i) => {
        if (filtro === 'todos') return true;
        return calcularSemaforo(i.stockActual, i.stockMinimo) === filtro;
      })
      .sort((a, b) => {
        // Críticos primero, luego bajos, luego ok, agotados al final.
        const orden: Record<Semaforo, number> = { critico: 0, bajo: 1, ok: 2, agotado: 3 };
        const oa = orden[calcularSemaforo(a.stockActual, a.stockMinimo)];
        const ob = orden[calcularSemaforo(b.stockActual, b.stockMinimo)];
        if (oa !== ob) return oa - ob;
        return a.nombre.localeCompare(b.nombre);
      });
  }, [insumos, busqueda, filtro]);

  const counts = useMemo(() => {
    const c = { todos: insumos.length, agotado: 0, critico: 0, bajo: 0, ok: 0 };
    for (const i of insumos) {
      c[calcularSemaforo(i.stockActual, i.stockMinimo)]++;
    }
    return c;
  }, [insumos]);

  const verBitacora = async (insumoId: number, nombre: string) => {
    const res = await window.api.insumos.bitacora(insumoId, 50);
    if (res.ok) setVistaBitacora({ insumoId, nombre, movs: res.data });
  };

  /**
   * Sprint 110 — Administrar lo que se vende es de Productos.
   *
   * Aquí había un `editarInsumo` (nombre, unidad, merma) y un `eliminarInsumo`. El borrado desde
   * esta pantalla dejó 41 ingredientes apagados que bloqueaban el alta por nombre y 27 productos
   * vendiéndose sin almacén detrás. La baja ahora vive en Productos y se lleva las dos mitades.
   */
  const verEnMenu = (i: InsumoVista) => navigate(`/productos?buscar=${encodeURIComponent(i.nombre)}`);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3" data-tour="inv-header">
        <div className="flex items-center gap-3">
          <Boxes className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Existencias</h1>
            {/*
              Sprint 110 — Se llamaba "Inventario" y hablaba de "insumos".

              Las dos palabras describían **nuestro modelo de datos**, no su trabajo: el negocio
              tiene productos con existencias, no un almacén de insumos paralelo a su menú. Cada vez
              que la interfaz decía "insumo" estaba pidiéndole a alguien que aprendiera cómo
              guardamos las cosas por dentro — y de ahí salieron 20 altas por la puerta equivocada.

              Aquí se dice **cuánto hay**. Lo que se vende se administra en Productos.
            */}
            <p className="text-sm text-slate-500">
              Cuánto te queda de cada cosa que vendes. Toca <strong>Agregar más stock</strong>{' '}
              cuando llegue mercancía.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/precargar-inventario')}
            className="btn-ghost"
            title="Sugerir cargas basadas en lo vendido ayer"
            data-tour="inv-precarga"
          >
            <Sparkles className="h-4 w-4" /> Precargar día
          </button>
          {/*
            Sprint 109 — Aquí ya no se da de alta nada.

            Este botón decía "Nuevo insumo" y creaba algo que **no se puede vender**: no sale en el
            punto de venta, ni en el menú en línea, ni para armar paquetes. Por ahí entraron la Pepsi
            y las Coca Cola del cliente, y nadie le avisó — se quedó buscando un botón que no iba a
            aparecer.

            Contradecía además una decisión ya tomada: desde el Sprint 82 el alta es *"doy de alta lo
            que vendo, con sus formatos, en una sola captura"*, y el par ingrediente↔producto **no se
            le expone al negocio**. Esa regla se aplicó en una dirección y esta puerta quedó abierta
            en la otra.

            Inventario **carga y corrige**: es donde se dice cuánto hay y cuánto se perdió. Lo que se
            vende nace en Productos, que es donde uno lo busca.
          */}
          <button onClick={() => navigate('/productos')} className="btn-primary">
            <Plus className="h-4 w-4" /> Dar de alta algo que vendes
          </button>
          <HelpButton tour={tourInventario} />
        </div>
      </header>

      {/*
        Sprint 109 — Lo que está en el almacén y no se puede vender, dicho donde se está mirando.
      */}
      <AvisoInvendibles />

      {insumos.length === 0 ? (
        <div className="card text-center py-8 text-slate-500">
          <Boxes className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Todavía no hay nada en tu inventario</p>
          <p className="text-sm mb-4">
            El inventario se llena solo: cada cosa que das de alta para vender —Sirloin, Pollo,
            Refresco— aparece aquí para cargarle existencias.
          </p>
          <button onClick={() => navigate('/productos')} className="btn-primary">
            <Plus className="h-4 w-4" /> Dar de alta lo primero que vendes
          </button>
        </div>
      ) : (
        <>
          {/*
            Sprint 118 — Un semáforo con todos los umbrales en cero nunca se pone en rojo.

            Encontrado al revisar la pantalla contra el tenant real: los 21 insumos decían "Alerta
            si baja a: 0". Con ese umbral, `calcularSemaforo` sólo puede devolver "agotado" o "ok",
            así que **los filtros "Críticos" y "Bajos" marcan 0 para siempre** y la pantalla informa
            "todo OK" incluso con el pollo por acabarse. La función de avisar existe, está bien
            hecha, y no puede dispararse nunca.

            No se inventa un número: **cuándo es "poco" el pollo lo decide el negocio**, y ponerle
            un valor por nuestra cuenta sería otra suposición disfrazada de oficio, que es
            exactamente lo que costó los Sprints 108 y 111. Lo que sí corresponde es **decirlo**:
            una función que no puede funcionar y no lo avisa es peor que no tenerla, porque el
            verde de "21 OK" se lee como una garantía.
          */}
          {(() => {
            /*
              El umbral del aviso es la MAYORÍA, y el número se dice.

              Primero puse `every` —"si ninguno tiene alerta"— mirando las ocho tarjetas que cabían
              en la captura, todas en 0, y dando por hecho las veintiuna. Al consultar los datos
              reales: **3 de 21 sí tenían umbral**, así que el aviso no salía nunca. Muestreo, no
              medición; el mismo error que este sprint vino a corregir en otras pantallas.

              `some` sería el extremo contrario: un regaño permanente porque falte una cosa suelta.
              Con la mayoría sin configurar, la pantalla informa "todo OK" sobre un semáforo que
              casi nunca puede encenderse — y ahí el aviso sí vale. Cuando la mayoría tenga su
              alerta, desaparece solo.
            */
            const sinAlerta = insumos.filter((i) => !i.stockMinimo).length;
            if (sinAlerta <= insumos.length / 2) return null;
            return (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3">
                <div className="text-sm font-semibold text-amber-900">
                  A {sinAlerta} de tus {insumos.length} cosas no les has dicho cuándo avisarte.
                </div>
                <div className="mt-0.5 text-sm text-amber-800">
                  Tienen la alerta en <strong>0</strong>, así que{' '}
                  <strong>de ésas sólo te aviso cuando ya se acabaron</strong> — no antes. Ponle a
                  cada una con cuánto quieres que te avise{' '}
                  <strong>tocando el número de «Alerta si baja a:»</strong> en su tarjeta — por
                  ejemplo, avísame del pollo cuando queden 3.
                  {/*
                    Sprint 124 — Antes esto mandaba al menú del negocio, y ALLÁ NO SE PODÍA: el
                    formulario de ingrediente sólo abre para crear uno nuevo. El aviso pedía algo
                    imposible. Ahora se arregla aquí mismo, en la tarjeta donde el dato se ve.
                  */}
                </div>
              </div>
            );
          })()}

          {/* Filtros rápidos + búsqueda */}
          <div className="card flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-tour="inv-filtros">
            <div className="flex flex-wrap gap-2">
              <ChipFiltro etiqueta="Todos" count={counts.todos} activo={filtro === 'todos'} color="slate" onClick={() => setFiltro('todos')} />
              <ChipFiltro etiqueta="🔴 Críticos" count={counts.critico} activo={filtro === 'critico'} color="red" onClick={() => setFiltro('critico')} />
              <ChipFiltro etiqueta="🟡 Bajos" count={counts.bajo} activo={filtro === 'bajo'} color="amber" onClick={() => setFiltro('bajo')} />
              <ChipFiltro etiqueta="⚫ Agotados" count={counts.agotado} activo={filtro === 'agotado'} color="slate" onClick={() => setFiltro('agotado')} />
              <ChipFiltro etiqueta="🟢 OK" count={counts.ok} activo={filtro === 'ok'} color="emerald" onClick={() => setFiltro('ok')} />
            </div>
            <div className="relative md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Buscar insumo…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
          </div>

          {/* Grid de cards */}
          {insumosFiltrados.length === 0 ? (
            <div className="card text-center py-8 text-slate-500">
              <p>Ningún ingrediente coincide con tu búsqueda o filtro.</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-tour="inv-tabla">
              {insumosFiltrados.map((i) => (
                <InsumoCard
                  key={i.id}
                  insumo={i}
                  onCargar={() => setCargaModal(i)}
                  onCorregir={() => setCorrigiendo(i)}
                  onMerma={() => setMermaModal({ insumoId: i.id, nombre: i.nombre, unidad: i.unidad })}
                  onBitacora={() => verBitacora(i.id, i.nombre)}
                  onVerEnMenu={() => verEnMenu(i)}
                  /* Sprint 124 — Al cambiar el umbral hay que repintar: el semáforo depende de él. */
                  onCambio={() => void cargar()}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Sprint 13.1 — Histórico del día por insumo (sin cambios) */}
      <div data-tour="inv-historico">
        <HistoricoDelDia />
      </div>

      {/* Modales */}
      {cargaModal && (
        <ModalCargaInsumo
          insumo={cargaModal}
          onClose={() => setCargaModal(null)}
          onOk={() => {
            setCargaModal(null);
            cargar();
          }}
        />
      )}

      {corrigiendo && (
        <ModalCorregirExistencia
          insumo={corrigiendo}
          onClose={() => setCorrigiendo(null)}
          onGuardado={() => cargar()}
        />
      )}

      {vistaBitacora && (
        <BitacoraModal
          titulo={`Bitácora — ${vistaBitacora.nombre}`}
          movs={vistaBitacora.movs}
          onClose={() => setVistaBitacora(null)}
        />
      )}

      {mermaModal && (
        <MermaModal
          insumoId={mermaModal.insumoId}
          nombre={mermaModal.nombre}
          unidad={mermaModal.unidad}
          onClose={() => setMermaModal(null)}
          onOk={() => {
            setMermaModal(null);
            cargar();
          }}
        />
      )}

    </div>
  );
}

// ============================================================================
// ChipFiltro — botón de filtro rápido con contador.
// ============================================================================

function ChipFiltro({
  etiqueta,
  count,
  activo,
  color,
  onClick,
}: {
  etiqueta: string;
  count: number;
  activo: boolean;
  color: 'slate' | 'red' | 'amber' | 'emerald';
  onClick: () => void;
}) {
  const colorMap = {
    slate:   'bg-slate-700 text-white border-slate-700',
    red:     'bg-red-600 text-white border-red-600',
    amber:   'bg-amber-500 text-white border-amber-500',
    emerald: 'bg-emerald-600 text-white border-emerald-600',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      /*
        Sprint 118 — 44 px. Medían 36, y son la forma de encontrar lo que falta comprar:
        con 21 insumos en pantalla, "🔴 Críticos" es el filtro que se aprieta a diario.
      */
      className={`min-h-[44px] rounded-full border-2 px-4 py-2 text-sm font-medium transition ${
        activo
          ? colorMap[color]
          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
      }`}
    >
      {etiqueta} <span className={`ml-1 text-xs ${activo ? 'opacity-90' : 'opacity-60'}`}>· {count}</span>
    </button>
  );
}

// ============================================================================
// BitacoraModal — sin cambios funcionales.
// ============================================================================

function BitacoraModal({
  titulo,
  movs,
  onClose,
}: {
  titulo: string;
  movs: MovimientoStockVista[];
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold">{titulo}</h3>
          <button onClick={onClose} className="btn-ghost">
            ✕
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1">Fecha</th>
                <th className="text-left px-2 py-1">Tipo</th>
                <th className="text-right px-2 py-1">Cantidad</th>
                <th className="text-left px-2 py-1">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {movs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-slate-400">
                    Sin movimientos
                  </td>
                </tr>
              ) : (
                movs.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100">
                    <td className="px-2 py-1 text-xs">{new Date(m.fecha).toLocaleString()}</td>
                    <td className="px-2 py-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${tipoColor(m.tipo)}`}>{m.tipo}</span>
                    </td>
                    <td className="px-2 py-1 text-right font-mono">{m.cantidad.toFixed(3)}</td>
                    <td className="px-2 py-1 text-xs text-slate-600">{m.motivo ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MermaModal — Sprint 34: añade 3 botones de razón rápida (un toque).
// ============================================================================

// Sprint 34 — Razones predefinidas. Tocar un botón llena `motivo` con la
// descripción larga. La dueña sigue pudiendo editar el campo libre, pero el
// 95% de las veces basta con un toque + cantidad + Registrar.
type RazonRapida = 'danado' | 'accidente' | 'regalo';
const RAZONES_RAPIDAS: Array<{
  id: RazonRapida;
  emoji: string;
  label: string;
  motivo: string;
}> = [
  { id: 'danado',    emoji: '🥩', label: 'Se dañó',          motivo: 'Producto dañado / no apto' },
  { id: 'accidente', emoji: '💥', label: 'Accidente',        motivo: 'Accidente o derrame' },
  { id: 'regalo',    emoji: '🎁', label: 'Regalo / cortesía', motivo: 'Regalo o cortesía a cliente' },
];

function MermaModal({
  insumoId,
  nombre,
  unidad,
  onClose,
  onOk,
}: {
  insumoId: number;
  nombre: string;
  unidad: string;
  onClose: () => void;
  onOk: () => void;
}) {
  const sesion = useSessionStore((s) => s.sesion);
  const [cantidad, setCantidad] = useState<number>(0);
  const [motivo, setMotivo] = useState('');
  const [razonSeleccionada, setRazonSeleccionada] = useState<RazonRapida | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const seleccionarRazon = (razon: typeof RAZONES_RAPIDAS[number]) => {
    setRazonSeleccionada(razon.id);
    setMotivo(razon.motivo);
  };

  const onGuardar = async () => {
    if (!sesion) return;
    setError(null);
    setEnviando(true);
    const res = await window.api.insumos.registrarMerma({
      insumoId,
      cantidad,
      motivo,
      usuarioId: sesion.usuarioId,
    });
    setEnviando(false);
    if (res.ok) onOk();
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-3">Registrar merma — {nombre}</h3>
        <div className="space-y-3">
          {/* Sprint 34 — Razones rápidas: un toque rellena el motivo. */}
          <div>
            <label className="label">¿Por qué?</label>
            <div className="grid grid-cols-3 gap-2">
              {RAZONES_RAPIDAS.map((r) => {
                const activa = razonSeleccionada === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => seleccionarRazon(r)}
                    className={`rounded-xl border-2 p-2 flex flex-col items-center gap-1 transition min-h-[72px] ${
                      activa
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    aria-pressed={activa}
                  >
                    <span className="text-2xl leading-none" aria-hidden="true">{r.emoji}</span>
                    <span className="text-xs font-medium text-slate-700 text-center leading-tight">{r.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="label">Cantidad ({unidad})</label>
            <NumberField
              min={0}
              decimals={3}
              className="input"
              value={cantidad}
              onChange={setCantidad}
            />
          </div>
          <div>
            <label className="label">Motivo (puedes editarlo)</label>
            <textarea
              className="input"
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Producto caducado, derrame, etc."
            />
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="btn-secondary">
              Cancelar
            </button>
            <button onClick={onGuardar} disabled={enviando || cantidad <= 0 || !motivo.trim()} className="btn-danger">
              <Plus className="h-4 w-4" /> Registrar merma
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sprint 13.1 — Histórico del día por insumo (trazabilidad operativa).
// Conservado tal cual; sólo cambió el contenedor superior.
// ============================================================================

function HistoricoDelDia() {
  const [abierto, setAbierto] = useState(false);
  const [fecha, setFecha] = useState<string>(() => toLocalYYYYMMDD(new Date()));
  const [datos, setDatos] = useState<HistoricoDiaVista | null>(null);
  const [cargando, setCargando] = useState(false);

  const cargar = async (f: string) => {
    setCargando(true);
    const res = await window.api.insumos.historicoDelDia(new Date(`${f}T12:00:00`).toISOString());
    setCargando(false);
    if (res.ok) setDatos(res.data);
  };

  useEffect(() => {
    if (abierto) cargar(fecha);
  }, [abierto, fecha]);

  const cambiarFecha = (deltaDias: number) => {
    const d = new Date(`${fecha}T12:00:00`);
    d.setDate(d.getDate() + deltaDias);
    setFecha(toLocalYYYYMMDD(d));
  };

  const esHoy = fecha === toLocalYYYYMMDD(new Date());

  return (
    <div className="card">
      <button
        onClick={() => setAbierto(!abierto)}
        /* Sprint 118 — 44 px: medía 24, y es lo que abre la trazabilidad del día. */
        className="flex min-h-[44px] w-full items-center justify-between py-1 text-left font-semibold"
        type="button"
      >
        <span className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Histórico del día
        </span>
        <span className="text-sm text-slate-500">{abierto ? '▼ Ocultar' : '▶ Ver'}</span>
      </button>

      {abierto && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => cambiarFecha(-1)} className="btn-ghost p-2" aria-label="Día anterior">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <input
              type="date"
              className="input text-center font-mono"
              value={fecha}
              max={toLocalYYYYMMDD(new Date())}
              onChange={(e) => setFecha(e.target.value)}
            />
            <button
              onClick={() => cambiarFecha(+1)}
              className="btn-ghost p-2"
              aria-label="Día siguiente"
              disabled={esHoy}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            {!esHoy && (
              <button
                onClick={() => setFecha(toLocalYYYYMMDD(new Date()))}
                className="btn-ghost text-xs"
              >
                Ir a hoy
              </button>
            )}
          </div>

          {cargando ? (
            <p className="text-center text-slate-400 py-4">Cargando...</p>
          ) : !datos ? (
            <p className="text-center text-slate-400 py-4">Sin datos</p>
          ) : datos.insumos.every((i) => i.cargado === 0 && i.vendido === 0 && i.mermado === 0 && i.ajustado === 0) ? (
            <p className="text-center text-slate-500 py-4 text-sm">
              No hubo movimientos de inventario el {datos.fecha}.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Ingrediente</th>
                    <th className="text-right px-3 py-2">Stock inicio</th>
                    <th className="text-right px-3 py-2 text-emerald-700">+ Cargado</th>
                    <th className="text-right px-3 py-2 text-blue-700">− Vendido</th>
                    <th className="text-right px-3 py-2 text-red-700">− Mermado</th>
                    <th className="text-right px-3 py-2 text-amber-700">± Ajuste</th>
                    <th className="text-right px-3 py-2 font-semibold">Stock fin</th>
                    <th className="text-right px-3 py-2 text-emerald-700">Para venta</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.insumos
                    .filter((i) => i.cargado > 0 || i.vendido > 0 || i.mermado > 0 || i.ajustado !== 0 || i.stockFin > 0)
                    .map((i) => (
                      <tr key={i.insumoId} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-medium">
                          {i.nombre}
                          <span className="block text-[10px] text-slate-400">
                            {i.unidad}
                            {i.mermaCoccionPct > 0 && ` · merma ${i.mermaCoccionPct.toFixed(0)}%`}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-500">
                          {i.stockInicio.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">
                          {i.cargado > 0 ? `+${i.cargado.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-blue-700">
                          {i.vendido > 0 ? `−${i.vendido.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-red-700">
                          {i.mermado > 0 ? `−${i.mermado.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-amber-700">
                          {i.ajustado !== 0 ? `${i.ajustado > 0 ? '+' : ''}${i.ajustado.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">
                          {i.stockFin.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700 font-semibold">
                          {i.paraVenta.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <p className="text-xs text-slate-500 mt-3 px-2">
                Convenciones: <strong>Stock inicio</strong> = lo que había al abrir el día.
                <strong> Stock fin</strong> = lo que quedó al cerrar. <strong>Para venta</strong> = stock fin
                aplicando merma de cocción (si tiene). Solo se muestran insumos con
                movimientos en la fecha.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function tipoColor(tipo: string): string {
  switch (tipo) {
    case 'CARGA_INICIAL':
      return 'bg-green-100 text-green-800';
    case 'VENTA':
      return 'bg-blue-100 text-blue-800';
    case 'MERMA':
      return 'bg-red-100 text-red-800';
    case 'AJUSTE':
      return 'bg-amber-100 text-amber-800';
    case 'CIERRE':
      return 'bg-slate-200 text-slate-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

